import { useCallback, useEffect } from "react";
import { useSearchParams } from "react-router-dom";
import { Box, Container, Divider, Typography, useTheme } from "@mui/material";
import { useLingui } from "@lingui/react";
import {
  PricingCardTag,
  WfButton,
  WfHeader,
  WfIconNavigationChevronLeftRegular,
  WfIconNavigationCloseRegular,
  WfPricingCard,
  WfSubscriptionFeaturesTable,
  Plan,
} from "@WahooFitness/redesignr";
import {
  ErrorBoundary,
  GlobalHeader,
  postClose,
  useDrawerContext,
  useHeaderContext,
} from "@WahooFitness/wahoo-offline-mfe";
import usePaywall from "./usePaywall";
import usePaywallDebug from "./usePaywallDebug";
import SubscriptionDebugInfo from "@/components/Membership/SubscriptionDebugInfo";
import { ClickTarget } from "@/services/Analytics/EventTypes";

// Deep-linked from a feature (?feature= present) → close via postClose(closeViewId); otherwise
// → navigateBack. isFeaturePaywall (the /paywall route) trims irrelevant tiers and owns the
// debug panel. Purchase success reuses this, except the inline Membership case no-ops rather
// than navigating back past a view that was never its own route (TAJ-895).
function PaywallContainer({
  closeViewId = "paywall",
  isFeaturePaywall = false,
}: {
  closeViewId?: string;
  isFeaturePaywall?: boolean;
}) {
  const { i18n } = useLingui();
  const { zIndex, palette } = useTheme();
  const { navigateBack } = useHeaderContext();
  const [searchParams] = useSearchParams();

  const isOpenedFromFeature = !!searchParams.get("feature");
  const handleClose = useCallback(() => postClose(closeViewId), [closeViewId]);
  const onPurchaseSuccess = isOpenedFromFeature ? handleClose : undefined;

  const { debugOpen, handleTierTap, handleDebugClose } = usePaywallDebug();
  const { setDrawer, handleClose: handleDrawerClose } = useDrawerContext();

  const {
    selectedPlan,
    proPkg,
    corePkg,
    showBase,
    showCore,
    isCoreSelectable,
    coreTrialAvailable,
    showRecommendedBadge,
    recommendedTier,
    currentTier,
    headerTitle,
    subtitleText,
    buttonText,
    disclaimerText,
    onPlanSelect,
    onCtaPress,
    logDismiss,
    purchaseInProgress,
    offeringsLoading,
    customerInfo,
    userSubscription,
  } = usePaywall(onPurchaseSuccess, isFeaturePaywall);

  const handleDismiss = useCallback(() => {
    logDismiss(isOpenedFromFeature ? ClickTarget.close : ClickTarget.back);
    (isOpenedFromFeature ? handleClose : navigateBack)();
  }, [logDismiss, isOpenedFromFeature, handleClose, navigateBack]);

  const closeDebugPanel = useCallback(() => {
    handleDrawerClose();
    handleDebugClose();
  }, [handleDrawerClose, handleDebugClose]);

  useEffect(() => {
    if (!debugOpen || !isFeaturePaywall) return;
    setDrawer({
      open: true,
      isPersistent: true,
      hideHeader: true,
      hidePuller: true,
      maxHeight: "70vh",
      children: (
        <SubscriptionDebugInfo
          userSubscription={userSubscription}
          customerInfo={customerInfo}
          onClose={closeDebugPanel}
        />
      ),
    });
  }, [debugOpen, isFeaturePaywall, userSubscription, customerInfo, closeDebugPanel, setDrawer]);

  // Close the drawer if the user navigates away while the debug panel is open.
  useEffect(() => {
    return () => {
      if (debugOpen && isFeaturePaywall) handleDrawerClose();
    };
  }, [debugOpen, isFeaturePaywall, handleDrawerClose]);

  function getPricingCardTag(tier: Plan) {
    if (currentTier === tier) return PricingCardTag.CURRENT_PLAN;
    if (showRecommendedBadge && recommendedTier === tier) return PricingCardTag.RECOMMENDED;
    return undefined;
  }

  return (
    <Box
      display="flex"
      flexDirection="column"
      height="100%"
      sx={{ backgroundColor: palette.background.level1 }}
    >
      <GlobalHeader
        primaryLeftActionItem={{
          icon: isOpenedFromFeature ? (
            <WfIconNavigationCloseRegular />
          ) : (
            <WfIconNavigationChevronLeftRegular />
          ),
          action: handleDismiss,
        }}
      />
      {/* Invisible tap target — 10 taps opens the debug panel. Feature paywall only. */}
      {isFeaturePaywall && (
        <Box
          position="fixed"
          top={0}
          left="30%"
          right="30%"
          height={56}
          zIndex={zIndex.appBar + 1}
          onClick={handleTierTap}
          sx={{ userSelect: "none" }}
        />
      )}
      <ErrorBoundary isLoading={offeringsLoading}>
        <Box flex={1} overflow="auto">
          <Container maxWidth="sm" disableGutters>
            <WfHeader hierarchy="H1" title={headerTitle} />
            <Box display="flex" flexDirection="column" gap={1.5} px={2} pb={2}>
              <Typography variant="prose-sm-medium" color="text.secondary">
                {subtitleText}
              </Typography>
              <WfPricingCard
                tier="pro"
                price={proPkg?.product.priceString ?? ""}
                i18nContext={i18n}
                tag={getPricingCardTag("pro")}
                isActivePlan={selectedPlan === "pro"}
                onClick={() => onPlanSelect("pro")}
              />

              {showCore && (
                <WfPricingCard
                  tier="core"
                  price={corePkg?.product.priceString ?? ""}
                  i18nContext={i18n}
                  trialAvailable={coreTrialAvailable}
                  tag={getPricingCardTag("core")}
                  isActivePlan={selectedPlan === "core"}
                  onClick={isCoreSelectable ? () => onPlanSelect("core") : undefined}
                />
              )}
              {showBase && (
                <Box sx={{ cursor: "default" }}>
                  <WfPricingCard
                    tier="base"
                    price="$0.00"
                    i18nContext={i18n}
                    tag={getPricingCardTag("base")}
                    isActivePlan={selectedPlan === "base"}
                  />
                </Box>
              )}
              {/* 20px margin + 12px parent gap = 32px between cards and table */}
              <Box mt={2.5}>
                <WfSubscriptionFeaturesTable
                  i18nContext={i18n}
                  selectedPlan={selectedPlan}
                  type="inherit"
                />
              </Box>
            </Box>
          </Container>
        </Box>
        <Divider />
        <Container maxWidth="sm" disableGutters>
          <Box
            px={2}
            pt={1.5}
            pb={4}
            display="flex"
            flexDirection="column"
            alignItems="center"
            gap={1.5}
          >
            <WfButton
              hierarchy="primary"
              size="large"
              fullWidth
              disabled={purchaseInProgress}
              loading={purchaseInProgress}
              onClick={onCtaPress}
            >
              {buttonText}
            </WfButton>
            {disclaimerText && (
              <Typography variant="ui-xs" color="text.secondary" textAlign="center">
                {disclaimerText}
              </Typography>
            )}
          </Box>
        </Container>
      </ErrorBoundary>
    </Box>
  );
}

export default PaywallContainer;
